// BookTickets/hooks/usePresaleStatus.ts
'use client'

import { useCallback, useSyncExternalStore } from 'react'

import type { Presale } from '@booking/types/bookingConfig'
import {
  nextPresaleChange,
  presaleNow,
  presaleNowOverride,
  presaleStatus,
  type PresaleStatus,
} from '@booking/lib/presale'

// Longer delays overflow setTimeout and fire straight away.
const MAX_TIMEOUT_MS = 2 ** 31 - 1

/**
 * The pre-sale's status, updated while the page stays open (so a tab left on
 * the "opens at midnight" notice switches to the flow at midnight). Null when
 * there is no pre-sale, and during server render and hydration, where the time
 * isn't the visitor's.
 */
export function usePresaleStatus(presale: Presale | undefined): PresaleStatus | null {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!presale || presaleNowOverride()) return () => {}

      let timer: ReturnType<typeof setTimeout> | undefined
      const scheduleNextChange = () => {
        const next = nextPresaleChange(presale, new Date())
        if (!next) return
        timer = setTimeout(
          () => {
            onChange()
            scheduleNextChange()
          },
          Math.min(next.getTime() - Date.now(), MAX_TIMEOUT_MS),
        )
      }

      scheduleNextChange()
      return () => clearTimeout(timer)
    },
    [presale],
  )

  return useSyncExternalStore(
    subscribe,
    () => (presale ? presaleStatus(presale, presaleNow()) : null),
    () => null,
  )
}
